import { produce } from 'immer'
import { create } from 'zustand'
import { ProjetosProvider } from 'src/providers/ProjetosProvider'
import { formatarComponentesCalcular } from 'src/components/projetos/utils/formatarComponentesCalcular'
import { ComponenteCalcular, EstruturaListagem, ListagemItem, ModuloPorRegiaoListagemResponse, ProjetoCalcular, ProjetoServico, VinculoModuloResponse } from 'src/components/projetos/types'
import {
  createDefaultProjetoPersonalizadoHibrido,
  DEFAULT_PROJETO_PERSONALIZADO_HIBRIDO_LOADINGS,
  DEFAULT_PROJETO_PERSONALIZADO_HIBRIDO_MODALS,
  DEFAULT_PROJETO_PERSONALIZADO_HIBRIDO_VALIDATIONS,
} from './personalizadoHibrido.constants'
import {
  ProjetoPersonalizadoHibridoData,
  ProjetoPersonalizadoHibridoLoadings,
  ProjetoPersonalizadoHibridoModals,
  ProjetoPersonalizadoHibridoValidations,
} from './personalizadoHibrido.types'
import { CONTROLADOR_MAXIMA_POTENCIA_FOTOVOLTAICA_ATTR, INVERSOR_CARREGADOR_TENSAO_MAXIMA_ENTRADA, INVERSOR_ONDA_POTENCIA_MAXIMA_ATTR, INVERTER_MAX_POWER_ATTR, INVERTER_MAX_POWER_INPUT_ATTR, MODULO_MODULO_TENSAO_VOC_ATTR, MODULO_POTENCIA_MAXIMA_ATTR, MODULO_POTENCIA_MINIMA_ATTR, MODULO_POWER_ATTR } from 'src/components/projetos/constants'

type State = {
  data: ProjetoPersonalizadoHibridoData
  validations: ProjetoPersonalizadoHibridoValidations
  loadings: ProjetoPersonalizadoHibridoLoadings
  modals: ProjetoPersonalizadoHibridoModals
}

type Actions = {
  changeValue: (values: Partial<ProjetoPersonalizadoHibridoData>) => void
  changeValidation: (values: Partial<ProjetoPersonalizadoHibridoValidations>) => void
  changeLoading: (values: Partial<ProjetoPersonalizadoHibridoLoadings>) => void
  changeModal: (values: Partial<ProjetoPersonalizadoHibridoModals>) => void
  reset: () => void
  prepararSalvar: () => ProjetoPersonalizadoHibridoData
  salvarProjeto: <T>(save: (payload: ProjetoPersonalizadoHibridoData) => Promise<T>) => Promise<T>
  addModulos: (modulos: ModuloPorRegiaoListagemResponse[], moduloSelecionado?: { id: number, quantidade: number }) => void
  addEstruturas: (estruturas: EstruturaListagem[], componentesProjeto?: { id: number, quantidade: number, grupo_id: number }[]) => void
  addAcessorios: (acessorios: ListagemItem[], componentesProjeto?: { id: number, quantidade: number }[]) => void
  addInversoresHibridos: (inversores: VinculoModuloResponse[], componentesProjeto?: { id: number, quantidade: number }[]) => void
}

type Store = { state: State; actions: Actions }

const projetosProvider = new ProjetosProvider()

const initialState = (): State => ({
  data: createDefaultProjetoPersonalizadoHibrido(),
  validations: { ...DEFAULT_PROJETO_PERSONALIZADO_HIBRIDO_VALIDATIONS },
  loadings: { ...DEFAULT_PROJETO_PERSONALIZADO_HIBRIDO_LOADINGS },
  modals: { ...DEFAULT_PROJETO_PERSONALIZADO_HIBRIDO_MODALS },
})

export const useProjetoPersonalizadoHibridoStore = create<Store>()((set, get) => {
  const update = (recipe: (store: Store) => void) => set(produce(recipe))

  return {
    state: initialState(),
    actions: {
      changeValue: values => update(({ state }) => { 
        Object.assign(state.data, values) 
      }),
      changeValidation: values => update(({ state }) => { Object.assign(state.validations, values)} ),
      changeLoading: values => update(({ state }) => { Object.assign(state.loadings, values) }),
      changeModal: values => update(({ state }) => { Object.assign(state.modals, values) }),
      reset: () => set({ state: initialState() }),
      prepararSalvar: () => JSON.parse(JSON.stringify(get().state.data)),
      salvarProjeto: async save => {
        get().actions.changeLoading({ salvarProjeto: true })
        try { return await save(get().actions.prepararSalvar()) }
        finally { get().actions.changeLoading({ salvarProjeto: false }) }
      },
      addModulos: (modulos: ModuloPorRegiaoListagemResponse[], moduloSelecionado?: { id: number, quantidade: number }) => {
        update(({ state }) => {
          const newModulos = modulos.map(modulo => {
            const potencia = modulo.atributos.find(item => item.atributo === MODULO_POWER_ATTR)
            const potenciaMaxima = modulo.atributos.find(item => item.atributo === MODULO_POTENCIA_MAXIMA_ATTR)
            const potenciaMinima = modulo.atributos.find(item => item.atributo === MODULO_POTENCIA_MINIMA_ATTR)
            const tensaoVocModulo = modulo.atributos.find(item => item.atributo === MODULO_MODULO_TENSAO_VOC_ATTR)

            return {
              potenciaMaxima: Number(potenciaMaxima?.valor || 0),
              potenciaMinima: Number(potenciaMinima?.valor || 0),
              tensaoVocModulo: Number(tensaoVocModulo?.valor || 0),
              componente_id: modulo.id,
              inversoresTipos: [],
              categoria_componentes_id: modulo.categoria_componentes_id,
              potencia: Number(potencia?.valor || 0),
              faturamento: modulo.data_previsao_faturamento ? new Date(modulo.data_previsao_faturamento).toISOString() : undefined,
              nome: modulo.nome,
              descricao: modulo.descricao,
              quantidade: moduloSelecionado?.quantidade || 0,
              categoria: modulo.categoria_componentes_nome,
              altura: modulo.altura,
              largura: modulo.largura,
              peso: modulo.peso,
              valor: 0, // Calculado no backend
              comprimento: modulo.comprimento,
              valor_unitario: 0,
              regioes_parametros: modulo?.regioes_parametros,
              finame: modulo?.finame,
              exibir_popup_parceiro: modulo.exibir_popup_parceiro,
              selecionado: modulo.id === moduloSelecionado?.id
            }
          })

          Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { modulosList: newModulos })
        })
      },
      addEstruturas: (estruturas: EstruturaListagem[], componentesProjeto?: { id: number, quantidade: number, grupo_id: number }[]) => {
        update(({ state }) => {
          const newEstruturas: EstruturaListagem[] = estruturas.map(
            (estrutura: EstruturaListagem) => {
              const itens = estrutura.items.map((item) => {
                const componenteProjeto = componentesProjeto?.find(
                  (componente) =>
                    Number(componente.id) === Number(item.componente_id) &&
                    Number(componente.grupo_id) === Number(estrutura.estruturas_grupo_id)
                );

                return {
                  ...item,
                  quantidade: componenteProjeto?.quantidade ?? 0,
                };
              });

              const itemSelecionado = itens.find((item) => Number(item.quantidade) > 0);
              const quantidadeItem = itens?.reduce((acumulador, item) => acumulador + item.quantidade, 0) || 0;

              return {
                ...estrutura,
                componente_id: itemSelecionado?.componente_id ?? 0,
                items: itens,
                opened: quantidadeItem > 0,
                quantidade: quantidadeItem,
              };
            }
          );

          Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { estruturas_edicao: newEstruturas })
        })
      },
      addAcessorios: (acessorios: ListagemItem[], componentesProjeto?: { id: number, quantidade: number }[]) => {
        update(({ state }) => {
          const newAcessorios: ListagemItem[] = acessorios.map((item) => {
            const componente = componentesProjeto?.find(i => i.id === item.componente_id)
            return {...item, quantidade: componente?.quantidade || 0}
          })

          Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { acessorios: newAcessorios })
        })
      },
      addInversoresHibridos: (inversores: VinculoModuloResponse[], componentesProjeto?: { id: number, quantidade: number }[]) => {
        update(({ state }) => {
          const newInversores: ListagemItem[] = inversores.map((item: VinculoModuloResponse): ListagemItem => {
            
            const potencia = item.atributos.find(item => item.atributo === INVERTER_MAX_POWER_ATTR);
            const potenciaMaximaEntradaCC = item.atributos.find(item => item.atributo === INVERTER_MAX_POWER_INPUT_ATTR);
            const tensaoMaximaEntrada = item.atributos.find(item => item.atributo === INVERSOR_CARREGADOR_TENSAO_MAXIMA_ENTRADA);
            const savedItem = componentesProjeto?.find(i => i?.id === item.id);

            return {
              potenciaMaximaEntradaCC: Number(potenciaMaximaEntradaCC?.valor || 0),
              tensaoMaximaEntrada: Number(tensaoMaximaEntrada?.valor || 0),
              maximaPotenciaFV: 0,
              componente_id: item.id,
              nome: item.nome,
              modelo: item?.modelo,
              descricao: item.descricao,
              quantidade: savedItem?.quantidade || 0,
              altura: item.altura,
              largura: item.largura,
              peso: item.peso,
              valor: 0,
              categoria_componentes_id: item.categoria_componentes_id,
              potencia: Number(potencia?.valor || 0),
              valor_unitario: 0,
              exibir_popup_parceiro: item?.exibir_popup_parceiro,
            }
          })

          Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { inversoresList: newInversores })
        })
      }
    },
  }
})
